import { useState } from 'react';
import { getTournaments, createTournament } from '../api/client';
import { useAuthStore } from '../store/auth';
import { useI18n } from '../i18n';
import { useLabels } from '../i18n/labels';
import { usePageTitle } from '../hooks/usePageTitle';
import { useUrlParam } from '../hooks/useUrlParam';
import { useLiveBouts } from '../hooks/useLiveBouts';
import { todayLocal } from '../utils/calendarDate';
import { sortByStart, tournamentPhase, type TournamentPhase } from '../utils/tournament';
import TournamentCard from '../components/tournament/TournamentCard';
import TournamentFormModal, { type TournamentFormData } from '../components/tournament/TournamentFormModal';
import Skeleton from '../components/shared/Skeleton';
import EmptyState from '../components/shared/EmptyState';
import AsyncContent from '../components/shared/AsyncContent';
import { useLoad } from '../hooks/useLoad';
import { ChevronDownIcon, TrophyIcon } from '../components/shared/Icons';
import { countryFilterOptions, flagEmoji } from '../lib/countries';
import type { Tournament, CreateTournamentDto } from '../types';

// The two jump buttons ("Upcoming (N)", "Past (N)").
const JUMP_CLS = 'inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200 hover:bg-gray-200 transition-colors cursor-pointer';

export default function TournamentList() {
  const { t } = useI18n();
  const { countryName } = useLabels();
  usePageTitle(t.nav.tournaments);
  const { hasRole } = useAuthStore();
  const isTournamentManager = hasRole('admin', 'tournament_manager');

  const [showCreate, setShowCreate]   = useState(false);
  // #595 — the filter is in the URL: Back and a reload keep it.
  const [filterCountry, setFilterCountry] = useUrlParam('country');
  // #457 — a failed load showed "No tournaments" on venue Wi-Fi.
  // #665, F02 — useLoad holds the loading, the error and Retry.
  const [liveKey, setLiveKey] = useState(0);
  const { data: tournaments, setData: setTournaments, loading, error: loadFailed, reload } = useLoad(
    () => getTournaments().then(r => r.data as Tournament[]),
    [],
    {
      context: 'TournamentList:tournaments',
      initial: [],
      // The live read starts again at each answer, also a Retry with the same tournaments.
      onData: () => setLiveKey(k => k + 1),
    },
  );

  async function handleCreate(data: TournamentFormData) {
    // TournamentFormModal enforces the required fields (name, date_start, type)
    // before calling onSubmit; narrow explicitly at the boundary.
    const res = await createTournament(data as CreateTournamentDto);
    setTournaments(ts => [res.data, ...ts]);
    setShowCreate(false);
  }

  // ── Bucketing ─────────────────────────────────────────────────────────────
  // "Today" = today falls within the tournament's date range (handles multi-day).
  // The phase split comes first and the country filter after it (#664, F29).
  const today = todayLocal();
  const inPhase = (phase: TournamentPhase) => tournaments.filter(tr => tournamentPhase(tr, today) === phase);

  // The live read takes the UNFILTERED list of today: a change of the filter must not fetch again.
  const liveBouts = useLiveBouts(inPhase('today'), liveKey);

  const matchesCountry = (tr: Tournament) => !filterCountry || tr.country === filterCountry;
  // #661 — the countries that the tournaments hold, and a country of the URL that none holds (#627).
  const countryOptions = countryFilterOptions(
    tournaments.map(tr => tr.country), filterCountry,
    c => `${flagEmoji(c)} ${countryName(c)}`,
  );

  const todayEvents = sortByStart(inPhase('today').filter(matchesCountry), 'asc');
  const upcoming    = sortByStart(inPhase('upcoming').filter(matchesCountry), 'asc');
  const past        = sortByStart(inPhase('past').filter(matchesCountry), 'desc');

  // #627 — a country of the URL (`?country=`) that no tournament has. The
  // list was blank, and the filter was hidden: the user could not clear it.
  const countryHidesAll = !!filterCountry && tournaments.length > 0
    && todayEvents.length + upcoming.length + past.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black">{t.nav.tournaments}</h1>
        {isTournamentManager && (
          <button onClick={() => setShowCreate(true)} className="btn-primary btn-sm" data-testid="tournament-create">
            + {t.tournament.create}
          </button>
        )}
      </div>

      {/* ── Country filter: shown if multiple countries are present, or
          while a filter is active (#627: also a country with no tournament). ── */}
      {(countryOptions.length > 1 || !!filterCountry) && (
        <div className="flex items-center gap-2">
          <label htmlFor="country-filter" className="text-xs text-gray-500">{t.country.label}:</label>
          <select
            id="country-filter"
            value={filterCountry}
            onChange={e => setFilterCountry(e.target.value)}
            className="input input-sm w-auto text-sm"
          >
            <option value="">{t.country.all}</option>
            {countryOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* ── Jump links ── #552: a button shape (32 px tall, an arrow), not a
          20 px label. */}
      {(upcoming.length > 0 || past.length > 0) && (
        <div className="flex gap-2">
          {upcoming.length > 0 && (
            <button
              onClick={() => document.getElementById('section-upcoming')?.scrollIntoView({ behavior: 'smooth' })}
              className={JUMP_CLS}>
              {t.common.upcoming} ({upcoming.length})
              <ChevronDownIcon className="w-3 h-3" />
            </button>
          )}
          {past.length > 0 && (
            <button
              onClick={() => document.getElementById('section-past')?.scrollIntoView({ behavior: 'smooth' })}
              className={JUMP_CLS}>
              {t.common.past} ({past.length})
              <ChevronDownIcon className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      {/* ── Create modal ── */}
      {showCreate && (
        <TournamentFormModal
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
        />
      )}

      <AsyncContent
        loading={loading}
        error={!!loadFailed}
        onRetry={reload}
        isEmpty={tournaments.length === 0 || countryHidesAll}
        skeleton={<Skeleton.List shape="tournament-card" count={6} />}
        empty={countryHidesAll ? (
          <EmptyState
            icon={<TrophyIcon className="w-12 h-12" />}
            title={t.tournament.noCountryMatch.replace('{country}', countryName(filterCountry))}
            action={
              <button type="button" data-testid="country-filter-clear" onClick={() => setFilterCountry('')} className="btn-outline btn-sm">
                {t.common.clear}
              </button>
            }
          />
        ) : <EmptyState
          icon={<TrophyIcon className="w-12 h-12" />}
          title={t.tournament.noTournaments}
          hint={t.common.empty.tournamentsHint}
          action={isTournamentManager ? (
            <button onClick={() => setShowCreate(true)} className="btn-primary btn-sm" data-testid="tournament-create-empty">
              + {t.tournament.create}
            </button>
          ) : undefined}
        />}
      >
        {/* The three sections are siblings, and a bare fragment gave them no
            spacing: the next heading sat directly against the previous
            section's last card. `space-y-8` separates the SECTIONS; the
            `space-y-3` inside each one still separates the cards. */}
        <div className="space-y-8">
          {/* ── Today ── */}
          {todayEvents.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-green-600 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse inline-block" />
                {t.common.today}
              </h2>
              {todayEvents.map(t => (
                <TournamentCard key={t.id} tournament={t} showBadges liveBouts={liveBouts[t.id] ?? []} isToday />
              ))}
            </div>
          )}

          {/* ── Upcoming ── */}
          {upcoming.length > 0 && (
            <div className="space-y-3">
              <h2 id="section-upcoming" className="text-xs font-bold uppercase tracking-wider text-gray-400 scroll-mt-20">{t.common.upcoming}</h2>
              {upcoming.map(t => (
                <TournamentCard key={t.id} tournament={t} showBadges />
              ))}
            </div>
          )}

          {/* ── Past ── */}
          {past.length > 0 && (
            <div className="space-y-3">
              <h2 id="section-past" className="text-xs font-bold uppercase tracking-wider text-gray-400 scroll-mt-20">{t.common.past}</h2>
              {past.map(t => (
                <TournamentCard key={t.id} tournament={t} showBadges />
              ))}
            </div>
          )}
        </div>
      </AsyncContent>

    </div>
  );
}

// TournamentForm removed — replaced by shared TournamentFormModal component
